import React, { useEffect } from 'react';
import { message, Form, Input, InputNumber, Row } from 'antd';
import TabularList from 'components/TabularList/TabularList';
import {
  getFormattedPhoneNumber,
  formatDollar,
  getPhoneNumberValidator,
  getPhoneEXTValidator,
  validateDates,
} from 'utils';
import { EMDASH } from 'constants';
import providerService, { PROVIDER_FILE_TYPES } from 'services/providers.service';
import Card, { CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import PhoneNumber from 'components/PhoneNumber';
import PhoneNumberContainer from 'components/PhoneNumberContainer';
import { DatePicker } from 'components/ui/DatePicker';
import dayjs from 'dayjs';
import { DATE_FORMAT_US_SLASH } from 'components/AriaDatePicker/AriaDatePicker';
import UploadFile from 'components/UploadFile/UploadFile';
import FormHelperText from 'components/ui/FormHelperText/FormHelperText';

const Insurance = ({ application, applicationFiles, setApplication, setApplicationFiles, isEditable = false }) => {
  const [form] = Form.useForm();

  // Sync insurance files from parent props
  const insuranceFiles = applicationFiles?.filter((f) => f.fileType === 'INSURANCE') || [];

  // Refresh files when applicationFiles change
  useEffect(() => {
    // This ensures the component re-renders when applicationFiles update
  }, [applicationFiles]);

  const { isEditing, editProps } = useEditCard({
    id: 'insurance',
    onEditStart: () =>
      form.setFieldsValue({
        liabilityInsurance: {
          id: application?.liabilityInsurance?.id,
          issuedBy: application?.liabilityInsurance?.issuedBy,
          coverageAmount: application?.liabilityInsurance?.coverageAmount,
          agentPhone: application?.liabilityInsurance?.agentPhone,
          agentPhoneExt: application?.liabilityInsurance?.agentPhoneExt,
          startDate: application?.liabilityInsurance?.startDate
            ? dayjs(application?.liabilityInsurance?.startDate).format('YYYY-MM-DD')
            : '',
          endDate: application?.liabilityInsurance?.endDate
            ? dayjs(application?.liabilityInsurance?.endDate).format('YYYY-MM-DD')
            : '',
        },
      }),
    onSave: async () => {
      await form.validateFields();
      if (insuranceFiles.length === 0) {
        message.error('Proof of insurance is required');
        throw new Error('Insurance file required');
      }
      const values = form.getFieldsValue();
      await providerService.updateApplication(application.id, { ...application, ...values });
      const refreshedApplication = await providerService.getApplication(application.id);
      setApplication(refreshedApplication);
      const refreshedFiles = await providerService.getApplicationFiles(application.id);
      setApplicationFiles(refreshedFiles);
      form.resetFields();
      message.success('Insurance updated');
    },
    onDiscard: () => {
      form.resetFields();
    },
  });

  return (
    <Card title="Insurance" extra={isEditable ? <CardEdit {...editProps} /> : null} noBodyPadding stickyHeader>
      <Form form={form} disabled={editProps.loading}>
        <Form.Item hidden name={['liabilityInsurance', 'id']}>
          <Input />
        </Form.Item>

        <TabularList
          indented
          data={[
            {
              title: 'Insurance provider',
              value: isEditing ? (
                <Form.Item
                  name={['liabilityInsurance', 'issuedBy']}
                  className="mb-0"
                  rules={[{ required: true, message: 'Insurance provider is required' }]}
                >
                  <Input placeholder="Enter insurance provider" className="max-w-72" />
                </Form.Item>
              ) : application?.liabilityInsurance?.issuedBy ? (
                application.liabilityInsurance.issuedBy
              ) : (
                EMDASH
              ),
              titleRequired: true,
            },
            {
              title: 'Insured amount',
              value: isEditing ? (
                <>
                  <Form.Item
                    name={['liabilityInsurance', 'coverageAmount']}
                    className="mb-0"
                    rules={[
                      { required: true, message: 'Insured amount is required' },
                      {
                        validator: (_, value) =>
                          value >= 500000
                            ? Promise.resolve()
                            : Promise.reject('Insured amount must be at least $500,000'),
                      },
                    ]}
                    validateFirst
                  >
                    <InputNumber
                      min={0}
                      formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => value?.replace(/\$\s?|(,*)/g, '')}
                      controls={false}
                      changeOnWheel
                      className="max-w-72"
                    />
                  </Form.Item>
                  <FormHelperText text="Insured amount must be at least $500,000" />
                </>
              ) : application?.liabilityInsurance?.coverageAmount ? (
                formatDollar(application.liabilityInsurance.coverageAmount)
              ) : (
                EMDASH
              ),
              titleRequired: true,
            },
            {
              title: 'Insurance provider phone',
              value: isEditing ? (
                <Row>
                  <PhoneNumberContainer
                    phoneNumber={
                      <Form.Item
                        name={['liabilityInsurance', 'agentPhone']}
                        className="mb-0"
                        rules={[
                          { required: true, message: 'Insurance provider phone is required' },
                          getPhoneNumberValidator('Invalid phone number'),
                        ]}
                      >
                        <PhoneNumber />
                      </Form.Item>
                    }
                    extension={
                      <Form.Item
                        name={['liabilityInsurance', 'agentPhoneExt']}
                        className="mb-0"
                        rules={[getPhoneEXTValidator('Invalid extension')]}
                      >
                        <Input placeholder="Extension" />
                      </Form.Item>
                    }
                  />
                </Row>
              ) : application?.liabilityInsurance?.agentPhone ? (
                getFormattedPhoneNumber(application.liabilityInsurance.agentPhone) +
                (application?.liabilityInsurance?.agentPhoneExt
                  ? ` ext. ${application.liabilityInsurance.agentPhoneExt}`
                  : '')
              ) : (
                EMDASH
              ),
              titleRequired: true,
            },
            {
              title: 'Start date',
              value: isEditing ? (
                <Form.Item
                  name={['liabilityInsurance', 'startDate']}
                  className="mb-0"
                  rules={[
                    { required: true, message: 'Start date is required' },
                    {
                      validator: validateDates(
                        form.getFieldValue,
                        ['liabilityInsurance', 'startDate'],
                        ['liabilityInsurance', 'endDate'],
                        'Start date must be before end date',
                      ),
                    },
                  ]}
                >
                  <DatePicker className="max-w-72" />
                </Form.Item>
              ) : application?.liabilityInsurance?.startDate ? (
                dayjs(application.liabilityInsurance.startDate).format(DATE_FORMAT_US_SLASH)
              ) : (
                EMDASH
              ),
              titleRequired: true,
            },
            {
              title: 'End date',
              value: isEditing ? (
                <Form.Item
                  name={['liabilityInsurance', 'endDate']}
                  className="mb-0"
                  rules={[
                    { required: true, message: 'End date is required' },
                    {
                      validator: validateDates(
                        form.getFieldValue,
                        ['liabilityInsurance', 'startDate'],
                        ['liabilityInsurance', 'endDate'],
                        'End date must be after start date',
                      ),
                    },
                  ]}
                >
                  <DatePicker className="max-w-72" />
                </Form.Item>
              ) : application?.liabilityInsurance?.endDate ? (
                dayjs(application.liabilityInsurance.endDate).format(DATE_FORMAT_US_SLASH)
              ) : (
                EMDASH
              ),
              titleRequired: true,
            },
            {
              title: 'Proof of insurance',
              value: (
                <UploadFile
                  fileCode={PROVIDER_FILE_TYPES.INSURANCE.code}
                  files={insuranceFiles}
                  setFiles={async (updatedFiles) => {
                    // Refresh files from server after upload/delete
                    const refreshedFiles = await providerService.getApplicationFiles(application.id);
                    setApplicationFiles(refreshedFiles);
                  }}
                  applicationId={application?.id}
                  disabled={editProps.loading}
                  readOnly={!isEditable}
                />
              ),
              titleRequired: true,
            },
          ]}
        />
      </Form>
    </Card>
  );
};

export default Insurance;
